import React, { FC, ReactNode, useState } from "react";
import { MenuRow } from "../DownloadMenu/DownloadMenuList";
import { MenuPopover, MenuTriggerVariant } from "../DownloadMenu/MenuPopover";
import { GlyphFileText, GlyphFolderDown, GlyphComment } from "../DownloadMenu/glyphs";
import type { CommentsFormat } from "../../utils/commentsExport";

const ROWS: {
  format: CommentsFormat;
  title: string;
  description: string;
  glyph: ReactNode;
}[] = [
  {
    format: "pdf",
    title: "Comments (PDF)",
    description: "Review comments as a formatted document",
    glyph: <GlyphFileText />,
  },
  {
    format: "csv",
    title: "Comments (CSV)",
    description: "Date, time, sender, comment and status",
    glyph: <GlyphFileText />,
  },
  {
    format: "all",
    title: "Comments (All content)",
    description: "Both formats, zipped",
    glyph: <GlyphFolderDown />,
  },
];

interface Props {
  onSelect: (format: CommentsFormat) => void;
  variant?: MenuTriggerVariant;
  busy?: boolean;
  glyphSize?: number;
  label?: string;
  tooltip?: ReactNode;
  stopPropagation?: boolean;
}

const defaultTooltip = (
  <div style={{ padding: "2px 1px" }}>
    <div
      style={{
        fontWeight: 700,
        fontSize: 12.5,
        color: "var(--accent-text)",
        marginBottom: 3,
      }}
    >
      Comments
    </div>
    <div style={{ fontSize: 11.5, lineHeight: 1.4, color: "var(--text-2)" }}>
      Download the review comments on this item
    </div>
  </div>
);

export const CommentsMenu: FC<Props> = ({
  onSelect,
  variant = "icon",
  busy = false,
  glyphSize,
  label = "Comments",
  tooltip,
  stopPropagation = true,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <MenuPopover
      open={open && !busy}
      onToggle={() => setOpen((v) => !v)}
      onClose={() => setOpen(false)}
      variant={variant}
      disabled={busy}
      label={busy ? "Preparing..." : label}
      tooltip={tooltip ?? defaultTooltip}
      triggerTitle="Download comments"
      disabledTitle="No comments available"
      glyph={(props) => <GlyphComment {...props} />}
      glyphSize={glyphSize}
      stopPropagation={stopPropagation}
    >
      <div role="menu" aria-label="Comment formats">
        <div
          className="uppercase text-muted"
          style={{
            fontSize: 9.5,
            letterSpacing: ".12em",
            padding: "12px 14px 8px",
            fontFamily: "CircularStdBlack",
          }}
        >
          Comments
        </div>
        <div className="flex flex-col gap-0.5" style={{ padding: "0 8px 10px" }}>
          {ROWS.map((row) => (
            <MenuRow
              key={row.format}
              glyph={row.glyph}
              title={row.title}
              description={row.description}
              disabled={false}
              onClick={() => {
                setOpen(false);
                onSelect(row.format);
              }}
            />
          ))}
        </div>
      </div>
    </MenuPopover>
  );
};

export default CommentsMenu;
